import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { AIComposer, AIStatePill } from '@ds/ai';
import { Badge, Button, Icon } from '@ds/core';
import { Alert, Drawer } from '@ds/feedback';
import { useAviso } from '@/comuns/toast';
import { ferramentaPorId } from '@/data/agentes';
import { dados } from '@/data/porta';
import type { Agente, MensagemDoAgente, PropostaDoAgente, RespostaDoAgente } from '@/data/tipos';

interface Turno {
    mensagem: MensagemDoAgente;
    resposta?: RespostaDoAgente;
    erro?: string;
}

/** "paciente" no papel, "você" na tela: quem digita aqui é a equipe testando. */
const ROTULO: Record<MensagemDoAgente['papel'], string> = {
    paciente: 'Você, no papel do paciente',
    agente: 'Agente',
};

function valorLegivel(valor: unknown): string {
    if (valor === null || valor === undefined) return '—';
    if (typeof valor === 'object') return JSON.stringify(valor);
    return String(valor);
}

/**
 * O simulador.
 *
 * É onde o agente é afinado antes de existir canal para ele: as consultas
 * rodam contra os **dados de verdade** da clínica, e o que ele propõe aparece
 * com os campos exatos e um botão de confirmar — que executa pelos mesmos
 * caminhos da tela, com a RLS de quem está logado.
 *
 * As ferramentas usadas ficam à vista de propósito. Um agente que "consultou a
 * agenda" sem mostrar o que leu é um agente em que ninguém consegue confiar
 * quando ele erra.
 */
export function SimuladorDoAgente({ agente, disponivel, onFechar }: {
    agente: Agente;
    disponivel: boolean;
    onFechar: () => void;
}) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [turnos, setTurnos] = useState<Turno[]>([]);
    const [texto, setTexto] = useState('');
    const [pensando, setPensando] = useState(false);
    const [confirmando, setConfirmando] = useState<string | null>(null);
    const [confirmadas, setConfirmadas] = useState<string[]>([]);

    const historico = (): MensagemDoAgente[] => turnos.flatMap((t) => [
        t.mensagem,
        ...(t.resposta ? [{ papel: 'agente' as const, texto: t.resposta.texto }] : []),
    ]);

    const enviar = async (entrada: string) => {
        const limpo = entrada.trim();
        if (!limpo || pensando) return;
        setTexto('');
        setPensando(true);

        const mensagem: MensagemDoAgente = { papel: 'paciente', texto: limpo };
        const anteriores = historico();
        setTurnos((atuais) => [...atuais, { mensagem }]);

        try {
            const resposta = await dados().conversarComAgente({
                tipo: agente.tipo === 'pre-atendimento' ? 'pre-atendimento' : 'atendimento',
                mensagens: [...anteriores, mensagem],
            });
            setTurnos((atuais) => atuais.map((t, i) => (i === atuais.length - 1 ? { ...t, resposta } : t)));
        } catch (e) {
            const erro = e instanceof Error ? e.message : 'Não foi possível falar com o agente.';
            setTurnos((atuais) => atuais.map((t, i) => (i === atuais.length - 1 ? { ...t, erro } : t)));
        } finally {
            setPensando(false);
        }
    };

    const confirmar = async (chave: string, proposta: PropostaDoAgente) => {
        setConfirmando(chave);
        try {
            const r = await dados().confirmarPropostaDoAgente(proposta);
            aviso({ title: `${proposta.rotulo} feito`, description: r.descricao, tone: 'success' });
            setConfirmadas((atuais) => [...atuais, chave]);
            revalidator.revalidate();
        } catch (e) {
            aviso({
                title: 'Não foi possível executar',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setConfirmando(null);
        }
    };

    return (
        <Drawer
            open
            title={`Simular ${agente.nome}`}
            subtitle={agente.tipo === 'pre-atendimento' ? 'Pré-atendimento' : 'Atendimento'}
            width={520}
            onClose={onFechar}
            header={<AIStatePill state={pensando ? 'executando' : 'sugerido'} label={pensando ? 'Pensando' : 'Pronto'} />}
            footer={(
                <AIComposer
                    value={texto}
                    onChange={setTexto}
                    onSend={enviar}
                    placeholder="Escreva como se fosse o paciente"
                    ariaLabel="Mensagem do paciente"
                    suggestions={turnos.length === 0
                        ? ['Quanto custa a limpeza de pele?', 'Tem horário na quinta à tarde?', 'Preciso remarcar minha consulta']
                        : []}
                    disclaimer="Simulação com os dados reais da clínica. Nada é agendado sem a sua confirmação."
                />
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {!disponivel && (
                    <Alert tone="warning" dismissible={false} title="Sem chave de IA, o agente não responde">
                        A configuração fica salva; o simulador passa a funcionar quando a plataforma ligar uma
                        chave de provedor.
                    </Alert>
                )}

                {agente.saudacao && turnos.length === 0 && (
                    <div style={{ padding: 12, borderRadius: 'var(--radius-md)', background: 'var(--surface-secondary)', border: '1px solid var(--border)', fontSize: 13 }}>
                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 4 }}>{ROTULO.agente}</span>
                        {agente.saudacao}
                    </div>
                )}

                {turnos.map((t, i) => (
                    <div key={`${i}-${t.mensagem.texto}`} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div>
                            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{ROTULO.paciente}</span>
                            <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600 }}>{t.mensagem.texto}</p>
                        </div>

                        {t.erro && <Alert tone="danger" dismissible={false} title="O agente não respondeu">{t.erro}</Alert>}

                        {!t.resposta && !t.erro && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                <Icon name="loader" size={14} /> pensando…
                            </span>
                        )}

                        {t.resposta && (
                            <>
                                {/* O que ele consultou fica à vista: agente que
                                    "olhou a agenda" sem mostrar o que leu é
                                    agente em que ninguém confia quando erra. */}
                                {t.resposta.usos.map((u, j) => (
                                    <div
                                        key={`${j}-${u.ferramenta}`}
                                        style={{
                                            padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                                            border: '1px dashed var(--border-strong)', fontSize: 12,
                                            color: 'var(--text-secondary)',
                                        }}
                                    >
                                        <Icon name="search" size={12} />{' '}
                                        Consultou <strong>{ferramentaPorId(u.ferramenta)?.nome ?? u.ferramenta}</strong>
                                        {Object.keys(u.argumentos).length > 0 && (
                                            <span> · {Object.entries(u.argumentos).map(([k, v]) => `${k}: ${valorLegivel(v)}`).join(' · ')}</span>
                                        )}
                                    </div>
                                ))}

                                <div style={{ padding: 12, borderRadius: 'var(--radius-md)', background: 'var(--surface-secondary)', border: '1px solid var(--border)', fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap' }}>
                                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 4 }}>{ROTULO.agente}</span>
                                    {t.resposta.texto}
                                    <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                                        <Badge tone="neutral">{t.resposta.modelo || t.resposta.provedor}</Badge>
                                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                                            {t.resposta.tokens.entrada + t.resposta.tokens.saida} tokens
                                        </span>
                                    </div>
                                </div>

                                {/* A regra do produto, na tela: os campos exatos
                                    à vista, e nada acontece antes do clique. */}
                                {t.resposta.propostas.map((p, j) => {
                                    const chave = `${i}-${j}-${p.ferramenta}`;
                                    const feita = confirmadas.includes(chave);
                                    return (
                                        <div
                                            key={chave}
                                            style={{
                                                padding: 12, borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--burgundy-200, var(--border-strong))',
                                                background: 'var(--surface-brand-soft)',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                                <Icon name="shield-alert" size={14} />
                                                <strong style={{ fontSize: 13, fontWeight: 600 }}>{p.rotulo}</strong>
                                                <Badge tone={feita ? 'success' : 'warning'}>
                                                    {feita ? 'Confirmado' : 'Aguardando confirmação'}
                                                </Badge>
                                            </div>

                                            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px', fontSize: 12.5 }}>
                                                {Object.entries(p.campos).map(([campo, valor]) => (
                                                    <span key={campo} style={{ display: 'contents' }}>
                                                        <dt style={{ color: 'var(--text-tertiary)' }}>{campo}</dt>
                                                        <dd style={{ margin: 0, fontWeight: 600 }}>{valorLegivel(valor)}</dd>
                                                    </span>
                                                ))}
                                            </dl>

                                            {!feita && (
                                                <div style={{ marginTop: 10 }}>
                                                    <Button
                                                        size="sm" disabled={confirmando === chave}
                                                        onClick={() => confirmar(chave, p)}
                                                    >
                                                        {confirmando === chave ? 'Executando…' : 'Confirmar e executar'}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                ))}

                {turnos.length > 0 && (
                    <span>
                        <Button size="sm" variant="ghost" onClick={() => { setTurnos([]); setConfirmadas([]); }}>
                            Recomeçar a conversa
                        </Button>
                    </span>
                )}
            </div>
        </Drawer>
    );
}
